import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import '@fontsource/eb-garamond/400.css'
import { CERTIFICATE_SIZE } from '@/lib/certificate'
import { lookup } from '@/lib/api'
import { hexContrast, isHex, normalizeHex, randomHex } from '@/lib/color'
import { fillCertificate, formatCertificateDate } from '@/lib/certificate'
import { certificateFontCss, downloadBlob, renderSvg, svgToPng } from '@/lib/certificateExport'
import { canvasToPdf } from '@/lib/pdf'
import { useSeo } from '@/components/usePageTitle'
import type { RegistryColor } from '@/lib/registry'
import { NoColor } from './ColorPage'

const EXAMPLE_SLUGS = new Set(['example', 'random', 'sample', 'test'])
const TEMPLATE_URL = '/certificate-template.svg'
const TYPEKIT = Boolean(import.meta.env.VITE_TYPEKIT_KIT_ID)

let templateCache: Promise<string> | null = null
function loadTemplate(): Promise<string> {
  if (!templateCache) {
    templateCache = fetch(TEMPLATE_URL).then((r) => {
      if (!r.ok) throw new Error('template')
      return r.text()
    })
  }
  return templateCache
}

interface CertColor {
  name: string
  hex: string
  slug: string
  regNum?: number | null
  regDate?: string | null
  reserved: boolean
}

function fromRegistry(c: RegistryColor): CertColor {
  return {
    name: c.name,
    hex: c.hex,
    slug: c.slug,
    regNum: c.regNum ?? null,
    regDate: c.registration,
    reserved: c.type === 'reserved',
  }
}

/**
 * The Official Certificate of Registration for one color, at
 * /color/<slug>/certificate.
 * Renders the original SVG artwork in the browser.
 */
export default function CertificatePage({ slug: slugProp }: { slug?: string }) {
  const params = useParams<{ slug: string }>()
  const slug = (slugProp ?? params.slug ?? 'example').toLowerCase()
  const isExample = EXAMPLE_SLUGS.has(slug)

  const example = useMemo<CertColor | null>(
    () =>
      isExample
        ? { name: slug.charAt(0).toUpperCase() + slug.slice(1) + ' Color', hex: randomHex(), slug: 'example', regNum: 1234567, regDate: '2000-01-01', reserved: false }
        : null,
    [isExample, slug],
  )
  const [fetched, setFetched] = useState<{ slug: string; color: CertColor | null } | null>(null)
  const [template, setTemplate] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    loadTemplate().then((t) => {
      if (alive) setTemplate(t)
    })
    return () => {
      alive = false
    }
  }, [])

  useEffect(() => {
    if (isExample) return
    let alive = true
    lookup(slug).then((c) => {
      if (alive) setFetched({ slug, color: c ? fromRegistry(c) : null })
    })
    return () => {
      alive = false
    }
  }, [slug, isExample])

  const color = example ?? (fetched && fetched.slug === slug ? fetched.color : 'loading')

  if (color === 'loading' || !template) {
    const bg = isHex(slug) ? normalizeHex(slug) : '#ffffff'
    return <div className="min-h-svh" style={{ backgroundColor: bg }} aria-busy="true" />
  }
  if (!color) return <NoColor slug={slug} />
  return <CertificateView color={color} template={template} />
}

function CertificateView({ color, template }: { color: CertColor; template: string }) {
  useSeo({
    title: `Certificate of Registration: ${color.name}`,
    description: `Official ColorMark certificate for ${color.name} (${color.hex}).`,
    path: `/color/${color.slug}/certificate`,
    image: `/api/swatch?hex=${color.hex.slice(1)}`,
    themeColor: color.hex,
  })
  const fg = hexContrast(color.hex)
  const [busy, setBusy] = useState<'png' | 'svg' | 'pdf' | null>(null)

  const input = useMemo(
    () => ({ name: color.name, hex: color.hex, regNum: color.regNum, regDate: color.regDate }),
    [color],
  )
  // On screen the page's own fonts apply; downloads embed them so the file is self-contained.
  const svg = useMemo(() => fillCertificate(template, input, { typekit: TYPEKIT }), [template, input])

  async function portable(): Promise<string> {
    return fillCertificate(template, input, { typekit: false, fontCss: await certificateFontCss() })
  }

  async function downloadSvg() {
    setBusy('svg')
    try {
      downloadBlob(new Blob([await portable()], { type: 'image/svg+xml' }), `colormark-certificate-${color.slug}.svg`)
    } finally {
      setBusy(null)
    }
  }

  async function downloadPng() {
    setBusy('png')
    try {
      downloadBlob(await svgToPng(await portable()), `colormark-certificate-${color.slug}.png`)
    } finally {
      setBusy(null)
    }
  }

  // Letter landscape at 300 dpi: the artwork is 3300 × 2550, so the page is exactly 11 × 8.5 in.
  async function downloadPdf() {
    setBusy('pdf')
    try {
      const canvas = await renderSvg(await portable(), CERTIFICATE_SIZE)
      downloadBlob(await canvasToPdf(canvas, { dpi: 300, title: `Certificate of Registration: ${color.name}` }), `colormark-certificate-${color.slug}.pdf`)
    } finally {
      setBusy(null)
    }
  }

  const btn =
    fg === 'white'
      ? 'border border-white/70 text-white hover:bg-white hover:text-black'
      : 'border border-black/60 text-black hover:bg-black hover:text-white'

  return (
    <div className="flex min-h-svh flex-col font-body print:block print:min-h-0" style={{ backgroundColor: color.hex, color: fg }}>
      <style>{`
        @media print {
          @page { size: letter landscape; margin: 0; }
          .noprint { display: none !important; }
          .cert-wrap { padding: 0 !important; background: #fff !important; }
          .cert-sheet { box-shadow: none !important; margin: 0 !important; max-width: none !important; width: 100vw; height: 100vh; }
          .cert-sheet svg { width: 100%; height: 100%; }
        }
      `}</style>

      <header className="noprint flex flex-wrap items-center justify-between gap-4 px-[36px] pt-[27px] pb-4">
        <Link to={`/color/${color.slug}`} className="font-display text-2xl no-underline hover:underline" style={{ color: fg }}>
          ← {color.name}
          <sup>CM</sup>
        </Link>
        <div className="flex flex-wrap gap-2 text-sm">
          <button type="button" onClick={() => window.print()} className={`rounded px-4 py-2 font-medium transition-colors ${btn}`}>
            Print
          </button>
          <button type="button" onClick={downloadPdf} disabled={busy !== null} className={`rounded px-4 py-2 font-medium transition-colors disabled:opacity-50 ${btn}`}>
            {busy === 'pdf' ? 'Rendering…' : 'Download PDF'}
          </button>
          <button type="button" onClick={downloadPng} disabled={busy !== null} className={`rounded px-4 py-2 font-medium transition-colors disabled:opacity-50 ${btn}`}>
            {busy === 'png' ? 'Rendering…' : 'Download PNG'}
          </button>
          <button type="button" onClick={downloadSvg} disabled={busy !== null} className={`rounded px-4 py-2 font-medium transition-colors disabled:opacity-50 ${btn}`}>
            {busy === 'svg' ? 'Preparing…' : 'Download SVG'}
          </button>
        </div>
      </header>

      <main className="cert-wrap flex-1 px-[36px] pb-6">
        <div
          className="cert-sheet mx-auto max-w-[1500px] bg-white shadow-[0_12px_48px_rgba(0,0,0,0.35)] [&>svg]:block [&>svg]:h-auto [&>svg]:w-full"
          // Original artwork with text placeholders substituted; values are escaped in fillCertificate.
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </main>

      <footer className="noprint flex flex-wrap items-center justify-between gap-3 px-[36px] pb-[27px] text-sm font-extralight">
        <span>
          {color.reserved
            ? `${color.name} is a reserved color and cannot be registered.`
            : color.regNum
              ? `Registration No. ${color.regNum.toLocaleString('en-US')}${color.regDate ? `, ${formatCertificateDate(color.regDate)}` : ''}`
              : 'Example certificate.'}
          {!color.reserved && color.slug !== 'example' && (
            <>
              {' · '}
              <Link to={`/registry?hex=${color.hex.slice(1)}`} style={{ color: fg }}>
                View in the registry
              </Link>
            </>
          )}
        </span>
        <Link to="/" className="no-underline" style={{ color: fg }}>
          Copyright © {new Date().getFullYear()} ColorMark™ All Rights Reserved
        </Link>
      </footer>
    </div>
  )
}
